import { useForm, Controller, useWatch, FormProvider } from "react-hook-form";
import FormWizardFormWrapper from "../../../Forms/FormWizard/FormWizardFormWrapper";
import PageFormWidget from "../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import PageFormButtons from "../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import useDashboardGroups, {
  DashboardItem,
} from "../../../CustomizedDashboard/useDashboardGroups";
import useAppViewNavigator from "../../../AppView/useAppViewNavigator";
import { buildDashboardPath } from "../../../CustomizedDashboard/dashboardUrl";
import { DASHBOARD_VISIBILITIES } from "../../../CustomizedDashboard/DashboardUtils";
import { useMemo } from "react";
import ScopeProvider from "../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import FilterAndScopeInputs from "../FilterAndScopeInputs/FilterAndScopeInputs";
import FilterProvider from "../../../AppView/FilterManagement/FilterProvider/FilterProvider";
import { filterEntityEnum } from "../../../AppView/FilterManagement/filterEntity";
import { AutomationWizardStepProps } from "../../AutomationsUtils";
import { LS_AUTOMATION_FILTER } from "../../../Findings/FindingsFilters/FilterUtils";
import "./DashboardStep.scss";
import { fileTypeIcons } from "../../../Findings/FindingDetailsPane/FindingsDetailsPaneComponents/Attachments/AttachmentsUtils";
import {
  CheckBox,
  FieldViewBlock,
  IconButton,
  useIcon,
  TextInput,
  SelectInput,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

type DashboardOptionGroup = {
  label: string;
  value: string;
  options: {
    label: string;
    value: number;
    customSuffix: React.ReactNode;
  }[];
};

const isSharedDashboard = (item: DashboardItem) =>
  item.visibility === DASHBOARD_VISIBILITIES.SHARED;

const DashboardStep: React.FC<AutomationWizardStepProps> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
}) => {
  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });
  const { handleSubmit, control, getValues } = methods;

  const filter = useWatch({ control, name: "filter" });
  const scope = useWatch({ control, name: "scope" });

  const { appViewNavigator } = useAppViewNavigator();
  const { dashboardGroups, dashboardLoading } = useDashboardGroups();
  const { getSvg } = useIcon();
  const dashboardOptions = useMemo(() => {
    return dashboardGroups?.reduce<DashboardOptionGroup[]>((groups, group) => {
      const options = group.items
        ?.filter(isSharedDashboard)
        .map((item: DashboardItem) => ({
          label: item.name,
          value: item.id,
          customSuffix: (
            <IconButton
              buttonStyle={"secondary"}
              size={"small"}
              isIcon={"seem-icon-expand"}
              type={`button`}
              data-analytics-event="user_click"
              data-analytics-props={`{"name": "open_dashboard_btn", "container": "dashboard_report_dashboard_picker"}`}
              onClick={() => {
                appViewNavigator(
                  filter?.value?.id,
                  scope?.value?.id,
                  !filter?.value?.id ? filter?.value?.query : null,
                  null,
                  true,
                  buildDashboardPath(item),
                  false
                );
              }}
            />
          ),
        }));

      if (options?.length) {
        groups.push({ label: group.name, value: group.name, options });
      }

      return groups;
    }, []);
  }, [dashboardGroups, filter, scope]);

  const getDashboardValue = (value: number) =>
    dashboardOptions
      ?.flatMap((group) => group?.options)
      ?.find((option) => option?.value === value);

  return (
    <FormProvider {...methods}>
      <FormWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <div
            className={`dashboard-step-container ${isSummary ? "is-summary" : ""}`}
            data-testid={"dashboard-step-container"}
          >
            <FilterProvider
              lsFilterKey={LS_AUTOMATION_FILTER}
              filtersEntity={filterEntityEnum.REMEDIATION_ITEM}
            >
              <ScopeProvider>
                <FilterAndScopeInputs isSummary={isSummary} />
              </ScopeProvider>
            </FilterProvider>
            <Controller
              name={"dashboard_id"}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => {
                return (
                  <SelectInput
                    label={"Dashboard"}
                    wrapperClassName="dashboard-choice"
                    disabled={!filter || !scope}
                    onChange={(value: Record<string, any>) => {
                      onChange(value?.value);
                    }}
                    data-testid={"dashboard-choice"}
                    labelTop
                    viewMode={isSummary}
                    value={getDashboardValue(parseInt(value))}
                    isSearchable
                    required
                    name={"dashboard-choice"}
                    error={error}
                    onBlur={onBlur}
                    isLoading={dashboardLoading}
                    options={dashboardOptions}
                    placeholder={"Select Dashboard"}
                  />
                );
              }}
              control={control}
              rules={{
                validate: (value: number | undefined | null) => {
                  if (!value || !getDashboardValue(value)) {
                    return REQUIRED_ERROR_MESSAGE;
                  }
                },
              }}
            />
            <Controller
              name="should_add_dashboard_link"
              control={control}
              defaultValue={true}
              render={({ field: { value, onChange } }) => {
                return (
                  <CheckBox
                    wrapperClassName="dashboard-link-checkbox"
                    name="should_add_dashboard_link"
                    data-testid="dashboard-link"
                    label="Include link back to Dashboard"
                    checked={value}
                    disabled={!filter || !scope}
                    viewMode={isSummary}
                    onChange={(_: boolean, newValue: boolean) => {
                      onChange(newValue);
                    }}
                    alignLeft={true}
                  />
                );
              }}
            />
            {!isSummary && (
              <div className="add-dashboard-link-label">
                Filters and Scope used for this report are shown in the PDF.
                This link leads to the default dashboard view.
              </div>
            )}
            <Controller
              name="name"
              control={control}
              rules={{ required: REQUIRED_ERROR_MESSAGE }}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => {
                return (
                  <TextInput
                    label="Name"
                    viewMode={isSummary}
                    labelTop
                    required
                    data-testid="name-input"
                    name="name"
                    placeholder="Report name"
                    error={error}
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                );
              }}
            />
            {isSummary && (
              <FieldViewBlock
                label={"Format"}
                className={"dashboard-step-format"}
              >
                <div className="dashboard-step-format-content">
                  <img
                    src={getSvg(fileTypeIcons.pdf)}
                    alt={fileTypeIcons.pdf}
                  />
                  <span>PDF</span>
                </div>
              </FieldViewBlock>
            )}
          </div>
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            data-testid={"dashboard-report-form"}
            nextButtonDisabled={!filter || !scope}
          />
        )}
      </FormWizardFormWrapper>
    </FormProvider>
  );
};

export default DashboardStep;
